import { useState } from "react";
import { toast } from "sonner";
import { Mail, KeyRound, Loader2, Eye, EyeOff, ArrowLeft } from "lucide-react";
import { forgotPassword, resetPassword } from "@/lib/afmApi";

const inputCls = "w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm text-black outline-none transition-colors placeholder:text-slate-400 focus:border-[#9E1B22]";
const strong = (p) => p.length >= 8 && /[A-Z]/.test(p) && /[0-9]/.test(p) && /[^A-Za-z0-9]/.test(p);

export const PasswordResetFlow = ({ defaultEmail = "", lockEmail = false, onDone }) => {
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState(defaultEmail);
  const [code, setCode] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);

  const sendCode = async () => {
    if (!email.trim()) { toast.error("Ingresa tu correo"); return; }
    setBusy(true);
    try { await forgotPassword(email.trim().toLowerCase()); toast.success("Te enviamos un código a tu correo"); setStep(2); }
    catch (e) { toast.error(e?.response?.data?.detail || "No se pudo enviar el código"); }
    finally { setBusy(false); }
  };

  const doReset = async () => {
    if (!code.trim()) { toast.error("Ingresa el código que recibiste"); return; }
    if (!strong(pw)) { toast.error("La contraseña debe tener 8+ caracteres, una mayúscula, un número y un símbolo"); return; }
    if (pw !== pw2) { toast.error("Las contraseñas no coinciden"); return; }
    setBusy(true);
    try { await resetPassword(email.trim().toLowerCase(), code.trim(), pw); toast.success("Contraseña actualizada. Inicia sesión con tu nueva clave."); onDone?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || "No se pudo actualizar la contraseña"); }
    finally { setBusy(false); }
  };

  if (step === 1) return (
    <div className="space-y-4" data-testid="pwreset-step-request">
      <div>
        <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-600">Correo electrónico</label>
        <div className="relative">
          <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input data-testid="pwreset-email-input" type="email" value={email} disabled={lockEmail} onChange={(e) => setEmail(e.target.value)} placeholder="tu@correo.com" className={`${inputCls} pl-9 ${lockEmail ? "bg-slate-100 text-slate-500" : ""}`} />
        </div>
      </div>
      <button data-testid="pwreset-send-code" onClick={sendCode} disabled={busy} className="flex w-full items-center justify-center gap-2 rounded-lg bg-black px-4 py-2.5 text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-60">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />} Enviar código
      </button>
      <button data-testid="pwreset-have-code" onClick={() => setStep(2)} className="w-full text-center text-xs font-medium text-slate-500 hover:text-black">Ya tengo un código</button>
    </div>
  );

  return (
    <div className="space-y-4" data-testid="pwreset-step-confirm">
      <div>
        <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-600">Código recibido por correo</label>
        <input data-testid="pwreset-code-input" value={code} onChange={(e) => setCode(e.target.value)} placeholder="Pega aquí el código" className={inputCls} autoComplete="one-time-code" />
      </div>
      <div>
        <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-600">Nueva contraseña</label>
        <div className="relative">
          <input data-testid="pwreset-new-password" type={show ? "text" : "password"} value={pw} onChange={(e) => setPw(e.target.value)} placeholder="••••••••" className={`${inputCls} pr-10`} autoComplete="new-password" />
          <button type="button" onClick={() => setShow((s) => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-black" data-testid="pwreset-toggle-visibility">{show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</button>
        </div>
        <p className="mt-1 text-[11px] text-slate-400">Mínimo 8 caracteres, con mayúscula, número y símbolo.</p>
      </div>
      <div>
        <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-600">Repite la nueva contraseña</label>
        <input data-testid="pwreset-confirm-password" type={show ? "text" : "password"} value={pw2} onChange={(e) => setPw2(e.target.value)} placeholder="••••••••" className={inputCls} autoComplete="new-password" />
      </div>
      <button data-testid="pwreset-submit" onClick={doReset} disabled={busy} className="flex w-full items-center justify-center gap-2 rounded-lg bg-[#9E1B22] px-4 py-2.5 text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-60">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />} Actualizar contraseña
      </button>
      <button data-testid="pwreset-back" onClick={() => setStep(1)} className="flex w-full items-center justify-center gap-1.5 text-xs font-medium text-slate-500 hover:text-black"><ArrowLeft className="h-3.5 w-3.5" /> Solicitar otro código</button>
    </div>
  );
};
